import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Ban, CheckCircle2, Loader2 } from "lucide-react";
import { z } from "zod";
import { useLanguage } from "@/hooks/use-language";
import { APPEAL_NO_ACCOUNT, ApiError, canAppeal, submitAppeal } from "@/lib/api";
import { getAppealSentAt, setAppealSentAt } from "@/lib/auth";
import { createErrorMessage } from "@/lib/error-message";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";

// Shown when the API answers "account_suspended" (login, token refresh, or
// any authenticated call), and with ?reason=ip for "ip_blocked".
// The appeal form needs no email: the server identifies the account from
// the appeal token (suspension) or the kept session (IP block) and the admin
// replies to the sign-up email. One appeal per 24h — this browser remembers
// it, and the server drops duplicates anyway.
export const Route = createFileRoute("/_auth/account-suspended")({
  validateSearch: z.object({ reason: z.enum(["ip"]).optional() }),
  component: AccountSuspendedPage,
});

const MAX_MESSAGE = 2000;
const APPEAL_COOLDOWN_MS = 24 * 60 * 60 * 1000;

function recentAppeal(): Date | null {
  const iso = getAppealSentAt();
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime()) || Date.now() - at.getTime() > APPEAL_COOLDOWN_MS) return null;
  return at;
}

function AccountSuspendedPage() {
  const { t, locale } = useLanguage();
  const { reason } = Route.useSearch();
  const isIP = reason === "ip";

  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sentAt, setSentAt] = useState<Date | null>(() => recentAppeal());
  const [justSent, setJustSent] = useState(false);
  const [noAccount, setNoAccount] = useState(() => !canAppeal());
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!message.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitAppeal({ message: message.trim(), kind: isIP ? "ip" : "account" });
      const now = new Date();
      setAppealSentAt(now.toISOString());
      setSentAt(now);
      setJustSent(true);
    } catch (err) {
      if (err instanceof ApiError && err.message === APPEAL_NO_ACCOUNT) {
        setNoAccount(true);
      } else {
        setError(createErrorMessage(err) || t("auth.appeal_error"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  const formatted = sentAt?.toLocaleString(locale === "ko" ? "ko-KR" : "en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  });

  return (
    <Card>
      <CardContent className="space-y-6 py-8">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex size-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <Ban className="size-6" />
          </div>
          <div className="space-y-1.5">
            <h1 className="text-lg font-semibold">
              {isIP ? t("auth.blocked_title") : t("auth.suspended_title")}
            </h1>
            <p className="text-sm break-keep text-muted-foreground">
              {isIP ? t("auth.blocked_desc") : t("auth.suspended_desc")}
              {!sentAt && !noAccount && (
                <>
                  <br />
                  {t("auth.appeal_intro")}
                </>
              )}
            </p>
          </div>
        </div>

        {sentAt ? (
          <div className="flex flex-col items-center gap-2 rounded-lg border bg-muted/40 px-4 py-6 text-center">
            <CheckCircle2 className="size-6 text-green-600" />
            <p className="text-sm font-medium">
              {justSent ? t("auth.appeal_sent_title") : t("auth.appeal_already_title")}
            </p>
            <p className="text-xs break-keep text-muted-foreground">{t("auth.appeal_sent_desc")}</p>
            {!justSent && formatted && (
              <p className="text-xs text-muted-foreground">
                {t("auth.appeal_already_at")}: {formatted}
              </p>
            )}
          </div>
        ) : noAccount ? (
          <p className="rounded-lg border bg-muted/40 px-4 py-4 text-center text-sm break-keep text-muted-foreground">
            {t("auth.appeal_no_account")}
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <div className="flex items-baseline justify-between">
                <Label htmlFor="appeal-message">{t("auth.appeal_message")}</Label>
                <span className="text-xs text-muted-foreground">
                  {message.length}/{MAX_MESSAGE}
                </span>
              </div>
              <textarea
                id="appeal-message"
                required
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE))}
                placeholder={t("auth.appeal_message_placeholder")}
                disabled={submitting}
                className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button
              type="submit"
              className="w-full cursor-pointer"
              disabled={submitting || !message.trim()}
            >
              {submitting && <Loader2 className="size-4 animate-spin" />}
              {t("auth.appeal_submit")}
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
